"use client";

import { PASSWORD_RULES } from "@repo/api/lib/validation";
import { cn } from "@ui/lib";
import { CheckIcon, DotIcon } from "lucide-react";

const RULES = [
	{
		label: `${PASSWORD_RULES.minLength}+ characters`,
		test: (p: string) => p.length >= PASSWORD_RULES.minLength,
	},
	{
		label: "A lowercase letter",
		test: (p: string) => PASSWORD_RULES.hasLowercase.test(p),
	},
	{
		label: "An uppercase letter",
		test: (p: string) => PASSWORD_RULES.hasUppercase.test(p),
	},
	{
		label: "A number or symbol",
		test: (p: string) => PASSWORD_RULES.hasNumberSymbolOrWhitespace.test(p),
	},
] as const;

export function isPasswordStrongEnough(password: string) {
	return RULES.every((rule) => rule.test(password));
}

/** Live checklist under a new-password field. Neutral until the rule is met. */
export function PasswordRulesChecklist({ password }: { password: string }) {
	return (
		<ul
			className="grid grid-cols-2 gap-x-3 gap-y-1"
			aria-label="Password rules"
		>
			{RULES.map((rule) => {
				const met = rule.test(password);
				return (
					<li
						key={rule.label}
						className={cn(
							"flex items-center gap-1 text-xs transition-colors",
							met ? "text-success" : "text-muted-foreground",
						)}
					>
						{met ? (
							<CheckIcon
								className="size-3.5 shrink-0"
								aria-hidden
							/>
						) : (
							<DotIcon
								className="size-3.5 shrink-0"
								aria-hidden
							/>
						)}
						<span>{rule.label}</span>
						<span className="sr-only">
							{met ? "(done)" : "(not yet)"}
						</span>
					</li>
				);
			})}
		</ul>
	);
}
